/**
 * Tenant-facing credits API contract (credit-wallet billing, Phase 4a).
 *
 * THE TWO-VIEW MONEY BOUNDARY (locked). Everything in this file is what a TENANT
 * is allowed to see: **credits and dates ONLY**. There is no µUSD, no USD, no
 * `*_micro`, no COGS / `provider_cost`, no markup, no margin — by construction.
 * The platform keeps money as exact integer µUSD internally (see
 * `IWalletBalance` in `credit-wallet.ts`); these DTOs are the projection at the
 * edge where the µUSD is dropped and only the display credit count survives.
 *
 * Real money is a SEPARATE, super-admin-only surface. If you ever find yourself
 * adding a `*Micro` / `*Usd` / `*Cost` field here, it belongs on the admin view
 * instead. The `credits-api` money-leak test scans every response body for
 * money-shaped keys and fails the build if one appears.
 *
 * Wire shape: credit figures are display-only `number`s (half-up against the peg);
 * every timestamp is an ISO-8601 `string`.
 */

import type { TCreditTransactionKind } from "./credit-wallet";

/**
 * A still-LIVE wallet lot as the tenant sees it — face credits, remaining credits,
 * and dates. No µUSD; the internal `amountMicro` / `remainingMicro` are dropped.
 */
export interface ICreditWalletLotView {
	/** `credit_transaction.id`. */
	id: number;
	/** Lot kind (`grant` | `purchase` for spendable lots). */
	kind: TCreditTransactionKind;
	/** Provenance tag, e.g. `monthly-grant` | `pack:<sku>` | `manual`. */
	source: string;
	/** When the lot was issued, ISO-8601. */
	issuedAt: string;
	/** Lapse instant, ISO-8601; `null` when the lot never expires. */
	expiresAt: string | null;
	/** Face credit count the lot was issued with. */
	credits: number;
	/** Unspent credits still available at `at`. */
	remainingCredits: number;
}

/**
 * An EXPIRED wallet lot as the tenant sees it: it lapsed on/before `at`, forfeiting
 * whatever credit was left. Credits + dates only; no µUSD.
 */
export interface ICreditWalletExpiredLotView {
	/** `credit_transaction.id`. */
	id: number;
	kind: TCreditTransactionKind;
	source: string;
	/** ISO-8601. */
	issuedAt: string;
	/** ISO-8601, or `null` when the lot never expires. */
	expiresAt: string | null;
	/** Face credit count the lot was issued with. */
	credits: number;
	/** Credits lost unspent when the lot lapsed. */
	forfeitedCredits: number;
}

/**
 * Tenant projection of the wallet balance — the credits-only view of
 * `IWalletBalance`. Every `*Micro` field of the internal snapshot
 * (`balanceMicro`, `creditUnitMicro`, per-lot `amountMicro`/`remainingMicro`, …)
 * is intentionally ABSENT.
 */
export interface ICreditWalletView {
	/** Spendable balance in credits at `at`. */
	balanceCredits: number;
	/** Remaining credits across live GRANT lots (the monthly, expiring allotment). */
	grantRemainingCredits: number;
	/** Remaining credits across live PURCHASE lots (prepaid packs). */
	packRemainingCredits: number;
	/** Projected date the balance hits zero at the trailing-30d burn, ISO-8601; `null` when not meaningful. */
	projectedZeroAt: string | null;
	/** The instant this balance was projected at, ISO-8601. */
	at: string;
	/**
	 * True only when credit enforcement is on. Off during the pilot, so an
	 * empty balance does not block new work. Not money.
	 */
	enforcementEnabled: boolean;
	/** Still-live lots, earliest-expiring first. */
	liveLots: ICreditWalletLotView[];
	/** Lots that lapsed on/before `at`, with their forfeiture (in credits). */
	expiredLots: ICreditWalletExpiredLotView[];
}

/**
 * A tenant credit statement over a window: how many CREDITS were consumed, broken
 * down per category, per action, and per meeting. Derived from
 * `usage_event.billable_micro` (platform-paid only) converted to credits at the
 * peg. No COGS, no USD, no µUSD.
 */
/**
 * Sentinel `meetingId` for the single aggregate row that carries the credits of
 * every meeting the caller is not allowed to see individually. Never a real
 * meeting id (real ids are uuid/graph ids), carries `subject: null`, and is only
 * ever emitted for access-restricted callers.
 */
export const OTHER_MEETINGS_BUCKET_ID = "__other_meetings__";

export interface ICreditStatement {
	/** Total credits consumed in the window. */
	totalCredits: number;
	/** Credits consumed per usage category (transcription | llm | image | …). */
	byCategory: Array<{
		category: string;
		/** Credits consumed by this category. */
		credits: number;
		/** Number of metered calls in this category. */
		callCount: number;
	}>;
	/**
	 * Credits consumed per metering `action` (e.g. `meeting_qa` = Ask AI,
	 * `generate_report`, `transcription`), so a single high-value feature can be
	 * surfaced as its own line even though it folds into a broader category.
	 * Same window / peg / half-up-per-row math as {@link byCategory}; the credits
	 * here overlap the category totals (an action is a slice of a category), so
	 * these rows do NOT re-sum to `totalCredits`.
	 */
	byAction: Array<{
		/** The `usage_event.action` tag (an internal, stable identifier). */
		action: string;
		/** Credits consumed by this action. */
		credits: number;
		/** Number of metered calls with this action. */
		callCount: number;
	}>;
	/**
	 * Credits consumed per meeting.
	 *
	 * ACCESS-SCOPED: a plain org member only gets rows for meetings they can
	 * actually reach (organizer / attendee / speaker) — the same predicate the
	 * meeting list uses. Everything else they may not see is rolled into ONE
	 * aggregate row keyed {@link OTHER_MEETINGS_BUCKET_ID} (no id, no subject) so
	 * the rows still add up to the org total. Super-admins and org owners/admins
	 * get the full per-meeting breakdown and never see that bucket.
	 */
	byMeeting: Array<{
		meetingId: string;
		/** Human meeting subject (decrypted at the read boundary), or `null` for a
		 *  deleted meeting / non-meeting usage the id no longer resolves to. */
		subject: string | null;
		/** Credits consumed by this meeting. */
		credits: number;
		/** Number of metered calls attributed to this meeting. */
		callCount: number;
		/** Latest usage in the window, ISO-8601, or null when undated. */
		occurredAt: string | null;
	}>;
}

/**
 * One row of the tenant credit ledger — the "trace every credit" view over
 * `credit_transaction`. Exposes the signed face `credits`, its kind, provenance,
 * and dates. The authoritative `amount_micro` / `credit_unit_micro` columns are
 * DROPPED; only a money-free subset of `metadata` (e.g. `seats`) is surfaced.
 */
export interface ICreditTransactionView {
	/** `credit_transaction.id`. */
	id: number;
	/** Ledger entry kind. */
	kind: TCreditTransactionKind;
	/** Signed face credit count (positive for grant/purchase; negative for expiry/adjustment). */
	credits: number;
	/** Provenance tag, e.g. `monthly-grant` | `pack:<sku>` | `manual` | `payment:<provider>`. */
	source: string;
	/** When the entry was issued, ISO-8601. */
	issuedAt: string;
	/** Lapse instant, ISO-8601; `null` for adjustments/refunds/never-expiring lots. */
	expiresAt: string | null;
	/** Seats the grant was sized for, when present in metadata. Money-free by design. */
	seats?: number;
}
